import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faThLarge,
  faTimes,
  faSignOutAlt,
  faCog,
  faUserGroup,
  faTasks,
  faStar,
  faBuilding,
  faCalendarAlt,
  faDollarSign,
  faMoneyBillWave,
  faChildReaching,
  faPlus,
  faPercent,
} from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import { routes } from "../../config/utility";
import logo from "../../assets/SECONDARYLOGO.svg";
import ThemeNavLink from "../atoms/ThemeNavLink";

const Sidebar = ({ isOpen, toggleSidebar, user, roles, setShowLogoutModal }) => {
  const renderNavItem = (item, key, { nested = false } = {}) => (
    <ThemeNavLink
      key={key}
      to={item.path}
      label={item.label}
      icon={item.icon}
      isOpen={isOpen}
      activeColor="bg-primary/10"
      hoverColor="bg-primary/5"
      textColor="text-gray-600"
      activeTextColor="text-primary"
      borderColor="border-secondary"
      className={
        nested
          ? isOpen
            ? "!py-2 !px-2.5 text-[13px]"
            : "!py-2"
          : "!my-0.5"
      }
    />
  );

  const renderNavGroup = (item, groupKey) => (
    <div
      key={groupKey}
      className={
        isOpen
          ? "rounded-xl border border-white/50 bg-white/30 shadow-sm px-2 py-2.5"
          : "py-0.5"
      }
    >
      {isOpen && (
        <p className="text-[11px] text-primary/90 uppercase font-semibold px-1.5 pb-2 mb-1 tracking-wide border-b border-primary/10">
          {item.group}
        </p>
      )}
      <div className={`flex flex-col ${isOpen ? "gap-0.5 pl-1" : "gap-1"}`}>
        {item.children.map((child, k) =>
          renderNavItem(child, `${groupKey}-${k}`, { nested: true })
        )}
      </div>
    </div>
  );

  // Organizer Menu Items - Simple Organization
  const organizerMenuItems = [
    {
      title: "Dashboard",
      items: [
        { icon: faThLarge, label: "Overview", path: routes.dashboard, allowedRoles: ["organizer"] }
      ],
    },
    {
      title: "Activities",
      items: [
        { icon: faTasks, label: "My Activities", path: routes.organizer_activities, allowedRoles: ["organizer"] },
        { icon: faPlus, label: "Add New Activity", path: routes.organizer_activity_new, allowedRoles: ["organizer"] },
        { icon: faCalendarAlt, label: "Bookings", path: routes.bookings, allowedRoles: ["organizer"] },
        { icon: faStar, label: "Reviews", path: routes.reviews, allowedRoles: ["organizer"] },
      ],
    },
    {
      title: "Finance",
      items: [
        { icon: faDollarSign, label: "Earnings", path: routes.commissions, allowedRoles: ["organizer"] },
        { icon: faMoneyBillWave, label: "Payouts", path: routes.payout_requests, allowedRoles: ["organizer"] },
        { icon: faPercent, label: "Fees & Deductions", path: routes.fees_deductions, allowedRoles: ["organizer"] },
      ],
    },
  ];

  // Admin Menu Items - Simple Organization
  const adminMenuItems = [
    {
      title: "Dashboard",
      items: [
        { icon: faThLarge, label: "Overview", path: routes.dashboard, allowedRoles: ["admin"] }
      ],
    },
    {
      title: "Accounts Management",
      items: [
        {
          group: "Customers",
          children: [
            { icon: faUserGroup, label: "Parents", path: routes.parents, allowedRoles: ["admin"] },
            { icon: faChildReaching, label: "Children", path: routes.children, allowedRoles: ["admin"] },
          ],
        },
        { icon: faBuilding, label: "Organizers", path: routes.organizers, allowedRoles: ["admin"] },
      ],
    },
   
    {
      title: "Content",
      items: [
        { icon: faTasks, label: "Activities", path: routes.activities, allowedRoles: ["admin"] },
        { icon: faCalendarAlt, label: "Bookings", path: routes.bookings, allowedRoles: ["admin"] },
        { icon: faStar, label: "Reviews", path: routes.reviews, allowedRoles: ["admin"] },
      ],
    },
    {
      title: "Finance",
      items: [
        { icon: faDollarSign, label: "Earnings", path: routes.commissions, allowedRoles: ["admin"] },
        { icon: faMoneyBillWave, label: "Payout Requests", path: routes.payout_requests, allowedRoles: ["admin"] },
      ],
    },
    {
      title: "Settings",
      items: [
        { icon: faCog, label: "System Settings", path: routes.setting, allowedRoles: ["admin"] },
      ],
    },
  ];

  // Support Admin menu — operational routes only (no Overview, Finance, or Settings)
  const assistantAdminMenuItems = [
    {
      title: "Operations",
      items: [
        { icon: faCalendarAlt, label: "Bookings", path: routes.bookings, allowedRoles: ["assistant_admin"] },
        { icon: faBuilding, label: "Organizers", path: routes.organizers, allowedRoles: ["assistant_admin"] },
        { icon: faTasks, label: "Activities", path: routes.activities, allowedRoles: ["assistant_admin"] },
        { icon: faStar, label: "Reviews", path: routes.reviews, allowedRoles: ["assistant_admin"] },
      ],
    },
    {
      title: "Accounts Management",
      items: [
        {
          group: "Customers",
          children: [
            { icon: faUserGroup, label: "Parents", path: routes.parents, allowedRoles: ["assistant_admin"] },
            { icon: faChildReaching, label: "Children", path: routes.children, allowedRoles: ["assistant_admin"] },
          ],
        },
        { icon: faBuilding, label: "Organizers", path: routes.organizers, allowedRoles: ["assistant_admin"] },
      ],
    },
  ];

  const menuItems = roles.includes("admin") 
    ? adminMenuItems 
    : roles.includes("assistant_admin")
    ? assistantAdminMenuItems
    : organizerMenuItems;

  return (
    <motion.div
      initial={false}
      animate={{ width: isOpen ? 260 : 70 }}
      className="bg-bodyMain/90 text-dark h-screen py-6 px-3 flex flex-col shadow-lg transition-all duration-300 border-secondary/10 border-r-2"
    >
      {/* Toggle Button */}
      <button
        onClick={toggleSidebar}
        className={`text-dark bg-white/10 hover:bg-white/20 px-2 py-1 rounded-full w-fit self-end  ${isOpen ? "mb-1" : "mb-12"} `}
      >
        <FontAwesomeIcon icon={isOpen ? faTimes : faBars} />
      </button>

      {/* Logo */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="mb-10 text-center"
          >
            <img src={logo} alt="Logo" className="w-32 h-12 mx-auto" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Navigation (Scrollable) */}
      <div className="flex-1 overflow-y-auto pr-1">
        <ul className="space-y-7">
          {menuItems.map((section, i) => (
            <li key={i} className="list-none">
              {isOpen && (
                <h4 className="text-xs text-grey uppercase font-semibold px-2 mb-3 tracking-wide">
                  {section.title}
                </h4>
              )}
              {!isOpen && i > 0 && <div className="my-2 border-t border-white/20" />}
              <div className={`flex flex-col ${isOpen ? "gap-2.5 px-0.5" : "gap-1.5"}`}>
                {section.items.map((item, j) => {
                  if (item.group && item.children) {
                    return renderNavGroup(item, `group-${i}-${j}`);
                  }
                  return renderNavItem(item, `item-${i}-${j}`);
                })}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Logout (Pinned Bottom) */}
      <div className="mt-6 flex-shrink-0">
        <button
          onClick={() => setShowLogoutModal(true)}
          className="w-full flex items-center justify-center gap-2 p-3 bg-red-900/30 hover:bg-red-500 rounded-lg text-sm font-semibold"
        >
          <FontAwesomeIcon icon={faSignOutAlt} />
          {isOpen && <span>Logout</span>}
        </button>
      </div>
    </motion.div>
  );
};

export default Sidebar;
